import { AfterViewInit, Component, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Router, ActivatedRoute } from '@angular/router';
import { FormValidatorService } from '../../../../../shared/services/form-validator.service';
import { Validators } from '../../../../../shared/validators';
import { CustomValidators } from '../../../../../shared/custom-validators';
import { LanguageService } from '../../../../../shared/services/language.service';
import { GetCustomerService } from '../../services/get-customer.service';
import { UpdateCustomerService } from '../../services/update-customer.service';
import { CreateCustomerService } from '../../services/create-customer.service';

@Component({
  selector: 'app-add-customer',
  templateUrl: './add-customer.component.html',
  styleUrls: ['./add-customer.component.scss'],
})
export class AddCustomerComponent implements OnInit, AfterViewInit {
  form!: FormGroup;
  success: boolean = false;
  editMode: boolean = false;
  formValidator = new FormValidatorService();
  validationError!: [
    {
      field: string;
      message: string;
      validation: string;
    },
  ];
  rolePermissions: any;
  selectedRole: any;
  id: string;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private translate: TranslateService,
    private languageService: LanguageService,
    private getCustomerService: GetCustomerService,
    private updateCustomerService: UpdateCustomerService,
    private createCustomerService: CreateCustomerService,
  ) {}

  async ngOnInit() {
    if (localStorage.getItem('language') === 'ar') {
      this.translate.use('ar');
    } else {
      this.translate.use('en');
    }
    this.languageService.languageUpdated.subscribe((lang) => {
      this.translate.use(lang);
    });
    this.activatedRoute.params.subscribe(async (val) => {
      this.id = val['id'];
      this.initForm();
      if (this.id) {
        await this.setupEditMode(this.id);
      }
    });
  }

  ngAfterViewInit() {
    this.translateValidationMessages();
    this.translate.onLangChange.subscribe(() => {
      this.translateValidationMessages();
    });
  }

  async onSubmit() {
    try {
      if (this.editMode) {
        this.editMode = false;
        const val = this.form.value;
        val['id'] = this.id;
        this.updateCustomerService.setParams(val);
        await this.updateCustomerService.make();
        this.router.navigate(['seller/customers/management']);
        return;
      }
      this.createCustomerService.setParams(this.form.value);
      await this.createCustomerService.make();
      this.router.navigate(['seller/customers/management']);
    } catch (errors) {
      console.log(errors);
      if (
        errors.faultyRequest.error &&
        errors.faultyRequest.error.message &&
        errors.faultyRequest.error.message.startsWith('The mobile number')
      ) {
        const customErrorMessage = this.translate.instant(
          'FormValidation.fieldExists', { field: "Phone", fieldAr: 'رقم الهاتف' }
        );
        this.formValidator.addError('mobile', 'customError', customErrorMessage);
        this.form.controls['mobile'].setErrors({ customError: customErrorMessage });
        this.form.controls['mobile'].markAsTouched();
        this.form.controls['mobile'].markAsDirty();
      } else if (
        errors.faultyRequest.error &&
        errors.faultyRequest.error.message &&
        errors.faultyRequest.error.message.startsWith('The customer business name')
      ) {
        const customErrorMessage = this.translate.instant(
          'FormValidation.fieldExists', { field: "Customer Business Name", fieldAr: 'اسم عمل العميل' }
        );
        this.formValidator.addError('customerBusinessName', 'customError', customErrorMessage);
        this.form.controls['customerBusinessName'].setErrors({ customError: customErrorMessage });
        this.form.controls['customerBusinessName'].markAsTouched();
        this.form.controls['customerBusinessName'].markAsDirty();
      } else if (errors.validationErrors) {
        this.validationError = errors.validationErrors.error;
        for (const e of this.validationError) {
          this.formValidator.addError(e.field, 'invalid', e.message);
          this.form.controls[e.field].setErrors({ invalid: e.message });
          this.formValidator.ShowErrors();
        }
      }
    }
  }

  translateValidationMessages() {
    this.translate.get('FormValidation').subscribe(() => {
      this.formValidator.validationMessages = {
        customerBusinessName: {
          required: this.translate.instant('FormValidation.Empty', {
            field: 'Customer Business Name',
            fieldAr: 'اسم عمل العميل',
          }),
          customPattern: this.translate.instant('FormValidation.disallowSpaces', {
            field: 'Customer Business Name',
            fieldAr: 'اسم عمل العميل',
          }),
          minlength: this.translate.instant('FormValidation.MinLength', {
            field: 'Customer Business Name',
            fieldAr: 'اسم عمل العميل',
            value: 5,
          }),
        },
        firstName: {
          required: this.translate.instant('FormValidation.Empty', {
            field: 'First name',
            fieldAr: 'الإسم الأول',
          }),
          customPattern: this.translate.instant('FormValidation.TextOnly', {
            field: 'First name',
            fieldAr: 'الإسم الأول',
          }),
          minLength: this.translate.instant('FormValidation.MinLength', {
            field: 'First name',
            fieldAr: 'الإسم الأول',
            value: 2,
          }),
          maxLength: this.translate.instant('FormValidation.MaxLength', {
            field: 'First name',
            fieldAr: 'الإسم الأول',
            value: 20,
          }),
        },
        lastName: {
          required: this.translate.instant('FormValidation.Empty', {
            field: 'Last name',
            fieldAr: 'الإسم الاخير',
          }),
          customPattern: this.translate.instant('FormValidation.TextOnly', {
            field: 'Last name',
            fieldAr: 'الإسم الاخير',
          }),
          minLength: this.translate.instant('FormValidation.MinLength', {
            field: 'Last name',
            fieldAr: 'الإسم الاخير',
            value: 2,
          }),
          maxLength: this.translate.instant('FormValidation.MaxLength', {
            field: 'Last name',
            fieldAr: 'الإسم الاخير',
            value: 20,
          }),
        },
        email: {
          required: this.translate.instant('FormValidation.Empty', {
            field: 'Email',
            fieldAr: 'البريد الإلكتروني',
          }),
          email: this.translate.instant('FormValidation.ValidEmail'),
        },
        mobile: {
          required: this.translate.instant('FormValidation.Empty', {
            field: 'Phone',
            fieldAr: 'رقم الهاتف',
          }),
          minlength: this.translate.instant('FormValidation.MinLength', {
            field: 'Phone',
            fieldAr: 'رقم الهاتف',
            value: 11,
          }),
          maxlength: this.translate.instant('FormValidation.MaxLength', {
            field: 'Phone',
            fieldAr: 'رقم الهاتف',
            value: 11,
          }),
          requiredNumber: this.translate.instant(
            'FormValidation.RequiredNumber',
          ),
          customPattern: this.translate.instant('FormValidation.numbersOnly', {
            field: 'Phone',
            fieldAr: 'رقم الهاتف',
          }),
        },
      };
    });
  }

  async initForm() {
    const textOnlyPattern =
      /^[\u0600-\u065F\u066A-\u06EF\u06FA-\u06FFa-zA-Z]+[\u0600-\u065F\u066A-\u06EF\u06FA-\u06FFa-zA-Z-_\.]*$/;
      const noSpacesXssPattern =/^(?!.*<[^>]+>)[\p{L}0-9]+$/u;
    const onlyNumberPattern = /^[0-9]*$/g;
    this.form = this.formValidator.createForm([
      {
        fieldName: 'customerBusinessName',
        defaultValue: null,
        validators: [
          {
            validatorFn: Validators.required,
            message: '',
          },
          {
            validatorFn: CustomValidators.customPattern(noSpacesXssPattern),
            message: '',
          },
          {
            validatorFn: Validators.minLength(5),
            message: '',
          },
        ],
      },
      {
        fieldName: 'firstName',
        defaultValue: null,
        validators: [
          {
            validatorFn: Validators.required,
            message: '',
          },
          {
            validatorFn: CustomValidators.customPattern(/(?!^\d+$)^.+$/g),
            message: '',
          },
          {
            validatorFn: CustomValidators.customPattern(textOnlyPattern),
            message: '',
          },
          {
            validatorFn: Validators.minLength(2),
            message: '',
          },
        ],
      },
      {
        fieldName: 'lastName',
        defaultValue: null,
        validators: [
          {
            validatorFn: Validators.required,
            message: '',
          },
          {
            validatorFn: CustomValidators.customPattern(/(?!^\d+$)^.+$/g),
            message: '',
          },
          {
            validatorFn: CustomValidators.customPattern(textOnlyPattern),
            message: '',
          },
          {
            validatorFn: Validators.minLength(2),
            message: '',
          },
        ],
      },
      {
        fieldName: 'email',
        defaultValue: null,
        validators: [
          {
            validatorFn: Validators.required,
            message: '',
          },
          {
            validatorFn: Validators.email,
            message: '',
          },
        ],
      },
      {
        fieldName: 'mobile',
        defaultValue: null,
        validators: [
          {
            validatorFn: Validators.required,
            message: '',
          },
          {
            validatorFn: CustomValidators.requiredNumber,
            message: '',
          },
          {
            validatorFn: Validators.minLength(11),
            message: '',
          },
          {
            validatorFn: Validators.maxLength(11),
            message: '',
          },
          {
            validatorFn: CustomValidators.customPattern(onlyNumberPattern),
            message: this.translate.instant('FormValidation.numbersOnly'),
          },
        ],
      },
      {
        fieldName: 'address',
        defaultValue: null,
        validators: [],
      },
      {
        fieldName: 'additionalInfo',
        defaultValue: null,
        validators: [],
      },
    ]);
  }

  convertControls(name: string) {
    return this.form.controls[name];
  }

  private async setupEditMode(id: string) {
    this.editMode = true;
    this.getCustomerService.setParams({ id });
    try {
      const response = await this.getCustomerService.make();

      this.form.controls['customerBusinessName'].setValue(
        response.data.customerBusinessName,
      );
      this.form.controls['firstName'].setValue(response.data.firstName);
      this.form.controls['lastName'].setValue(response.data.lastName);
      this.form.controls['email'].setValue(response.data.email);
      this.form.controls['mobile'].setValue(response.data.mobile);
      this.form.controls['address'].setValue(response.data.address);
      this.form.controls['additionalInfo'].setValue(
        response.data.additionalInfo,
      );
    } catch (e) {
      console.log(e);
      this.router.navigate(['/seller/customers/management']);
    }
  }
}
